import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { PAYMENTS_ENABLED } from '../../lib/config'

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-stone font-body text-navy">
      <nav className="sticky top-0 z-40 bg-navy text-stone px-6 py-3 flex items-center justify-between border-b-2 border-golden">
        <Link to="/" className="flex items-center gap-2 hover:text-stone/80 transition">
          <ArrowLeft size={18} />
          <span className="hl text-xl text-stone">Day One</span>
        </Link>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        <h1 className="hl text-[clamp(1.6rem,4.4vw,2.6rem)] mb-2">Terms of Service</h1>
        <p className="text-navy/70 text-sm mb-4">Last updated: March 3, 2026</p>
        <p className="text-navy/70 text-sm mb-10 italic">
          This is a ~5 minute read. We wrote it in plain language because nobody benefits from
          terms you can't understand.
        </p>

        <div className="prose prose-navy max-w-none space-y-8 text-navy/80 leading-relaxed">

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">What This Covers</h2>
            <p>
              These terms govern your use of Day One, a web-based team onboarding
              workshop built by <strong>MVP Club Consulting</strong> ("we," "us," "our"), based in Denver, Colorado.
            </p>
            <p>
              By using Day One, you agree to these terms. If you're using it on behalf of a company,
              you're agreeing on their behalf and confirming you have authority to do so.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">What Day One Is</h2>
            <p>
              Day One is a browser-based workshop platform where teams go through structured onboarding
              sessions run by AI facilitators. You create or join sessions, chat in real time, upload
              documents for AI review, and receive synthesized outputs from the workshop.
            </p>
            <p>
              There is nothing to install. It runs entirely in your web browser.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Your Account</h2>
            <p>
              Every session, solo or team, is tied to a signed-in account with a verified email
              address. You sign in with Google or with an email address and password, and a
              password account confirms its address before it can start a session. Teammates you
              invite join your room with the room code and password, and need no account of their
              own. You're
              responsible for keeping your login credentials secure. If you suspect unauthorized access,
              let us know at{' '}
              <a href="mailto:security@mvpclub.ai" className="text-amber hover:text-amber-dark underline">
                security@mvpclub.ai
              </a>.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Your Data: You Own It</h2>
            <p>
              Everything you type, upload, or create in Day One belongs to you. Your session messages,
              uploaded documents, reflection responses, and generated artifacts are yours.
            </p>
            <p>
              We need a limited license to operate the service: you grant us permission to store, process,
              and display your content so the workshop can function. We will never use your
              content for marketing, sell it, or share it with third parties beyond what's needed to run
              the service.
            </p>
            <p>
              Specifically, your session data is sent to OpenRouter, Inc., which routes it to Modal, the
              inference host for the model that runs your workshop. If that path fails or is too slow, the
              turn goes to Anthropic's Claude API instead. These services process data under their own
              terms, and importantly,{' '}
              <strong>they do not train models on data submitted through the API</strong>.
              {PAYMENTS_ENABLED && ' Billing details are sent to Stripe for payment processing.'}
            </p>
            <p>
              If your organization needs the processing terms in contract form, our{' '}
              <Link to="/dpa" className="text-amber hover:text-amber-dark underline">
                Data Processing Agreement
              </Link>{' '}
              is published pre-signed and takes effect when you use Day One, with no countersignature required.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">AI Facilitation: What It Is and Isn't</h2>
            <p>
              Day One uses large language models (GLM 5.3 through OpenRouter, with Anthropic's Claude as the fallback, as listed in our Data Processing Agreement) to run your workshop sessions. The AI
              asks questions, synthesizes conversation, and summarizes findings. It's a tool to help your
              team think together, not a replacement for professional judgment.
            </p>
            <p className="font-semibold text-navy">
              AI outputs are not professional advice. They are not legal, HR, financial, medical, or
              consulting advice. Treat them as a starting point for your team's thinking. We are not
              liable for decisions made based on AI-generated content.
            </p>
            <p>
              The AI agents are separate instances. They don't share memory across sessions or stages,
              and they're transparent about this during the workshop.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Cost</h2>
            <p>
              Day One is free. Solo sessions, team sessions and every deliverable are included at no
              cost, and we do not ask for or collect payment details. To keep the service available
              to everyone, session creation is subject to daily and overall usage limits.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Acceptable Use</h2>
            <p>
              We trust our users, but we need some ground rules. Please don't:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Use Day One for anything illegal</li>
              <li>Upload content that's harmful, harassing, or that you don't have rights to</li>
              <li>Attempt to reverse-engineer, probe, or extract the AI system or its prompts</li>
              <li>Try to circumvent payment or access controls</li>
              <li>Use automated tools to scrape or overload the service</li>
            </ul>
            <p>
              If we find a violation, we may suspend or terminate your access. We'll try to give you
              notice first unless the situation is urgent.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Where Your Data Lives</h2>
            <p>
              All session data is stored in <strong>Google Cloud Firestore</strong> in the us-east1 region.
              Google encrypts data at rest and in transit. We chose Google Cloud because their infrastructure
              security is world-class. It's the same platform that runs Gmail, Google Docs, and YouTube.
            </p>
            <p>
              Session content is not copied into any secondary database or analytics warehouse.
              The only other services that touch your data are the subprocessors we list: the AI
              provider that powers the agents, our payment processor, our email provider, and our
              error monitor. Each one, what it receives, and where it operates is documented in our{' '}
              <Link to="/security" className="text-amber hover:text-amber-dark underline">
                Security &amp; Trust
              </Link>{' '}
              page and our{' '}
              <Link to="/privacy" className="text-amber hover:text-amber-dark underline">
                Privacy Policy
              </Link>.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Cancellation and Data Deletion</h2>
            <p>
              You can stop using Day One at any time. If you want your data deleted, email{' '}
              <a href="mailto:privacy@mvpclub.ai" className="text-amber hover:text-amber-dark underline">
                privacy@mvpclub.ai
              </a>{' '}
              and we'll remove all your personal data and session history within 30 days.
            </p>
            <p>
              If we terminate your access for a terms violation, we'll retain your data for 30 days so
              you can request an export, then delete it.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Service Availability</h2>
            <p>
              We work hard to keep Day One running reliably, but we can't guarantee 100% uptime.
              Like all software, it may occasionally have bugs or downtime. We don't offer a formal SLA
              at this stage, but we do care deeply about reliability and will communicate proactively
              about any issues.
            </p>
            <p className="font-semibold text-navy">
              Day One is provided "as is." We disclaim all warranties, express or implied, including
              warranties of merchantability, fitness for a particular purpose, and non-infringement.
            </p>
            <p>
              Why this clause? We're a small company offering Day One at no cost, and we limit our legal
              exposure accordingly. This is standard for SaaS, not a sign that we don't care about quality.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Limitation of Liability</h2>
            <p>
              Our total liability to you for any claim arising from your use of Day One is limited to
              the amount you've paid us in the 12 months before the claim. We are not liable for
              indirect, incidental, or consequential damages, including lost profits, lost data, or
              business interruption.
            </p>
            <p>
              This isn't because we don't stand behind our work. It's because open-ended liability
              is incompatible with running an affordable service. We'd rather be transparent about this
              than bury it in fine print.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Indemnification</h2>
            <p>
              If your use of Day One results in a legal claim against us from a third party (for example,
              you uploaded content you didn't have rights to), you agree to cover our reasonable legal costs.
              This only applies to claims caused by your actions, not by normal use of the service.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Changes to These Terms</h2>
            <p>
              We may update these terms from time to time. For significant changes, we'll send a notice
              to the email address associated with your account at least 30 days before the changes
              take effect. Continued use after that notice period means you accept the updated terms.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Governing Law</h2>
            <p>
              These terms are governed by the laws of the State of Colorado. Any disputes will be resolved
              in the courts of the City and County of Denver, Colorado. We both agree to try to resolve
              disputes informally first. Email us and we'll work it out.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Contact</h2>
            <p>
              Questions about these terms? We're real people and we respond to email.
            </p>
            <ul className="list-none pl-0 space-y-1 mt-3">
              <li><strong>General:</strong>{' '}
                <a href="mailto:hello@mvpclub.ai" className="text-amber hover:text-amber-dark underline">
                  hello@mvpclub.ai
                </a>
              </li>
              <li><strong>Privacy:</strong>{' '}
                <a href="mailto:privacy@mvpclub.ai" className="text-amber hover:text-amber-dark underline">
                  privacy@mvpclub.ai
                </a>
              </li>
              <li><strong>Security:</strong>{' '}
                <a href="mailto:security@mvpclub.ai" className="text-amber hover:text-amber-dark underline">
                  security@mvpclub.ai
                </a>
              </li>
            </ul>
            <p className="text-sm text-navy/70 mt-4">
              MVP Club Consulting<br />
              Denver, CO
            </p>
          </section>

        </div>
      </div>
    </div>
  )
}
